import React from 'react';

const SORTS = [
  { key: 'inquiries', label: 'Inquiries' },
  { key: 'won', label: 'Orders' },
  { key: 'conversion', label: 'Conversion' },
];
const HEAD_CLS = 'text-left text-[10px] uppercase tracking-wider text-earth/60 font-sans font-semibold px-3 py-2 whitespace-nowrap';
const CELL_CLS = 'px-3 py-2.5 text-xs font-sans text-earth/70 whitespace-nowrap';

const slugOf = (country) => country.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const formatDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export const sortCountries = (rows, key) =>
  [...rows].sort((a, b) => (b[key] - a[key]) || (b.inquiries - a.inquiries) || (b.won - a.won) || a.country.localeCompare(b.country));

function SortPills({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-earth/60 font-sans mr-1">Sort by</span>
      {SORTS.map(s => (
        <button key={s.key} onClick={() => onChange(s.key)} className={`text-[10px] font-sans px-2.5 py-1 rounded-md transition-colors ${value === s.key ? 'bg-earth-dark text-white font-semibold' : 'bg-white text-earth/50 border border-earth/15 hover:border-saffron/30'}`} data-testid={`country-sort-${s.key}`}>
          {s.label}
        </button>
      ))}
    </div>
  );
}

function CountryRow({ row, maxInquiries }) {
  return (
    <tr className="border-t border-earth/5 hover:bg-cream/40 transition-colors" data-testid={`country-row-${slugOf(row.country)}`}>
      <td className="px-3 py-2.5 text-xs font-sans font-semibold text-earth-dark whitespace-nowrap">{row.country}</td>
      <td className={CELL_CLS}>
        <div className="flex items-center gap-2 min-w-[110px]">
          <span className="font-semibold text-earth-dark w-6" data-testid={`country-inquiries-${slugOf(row.country)}`}>{row.inquiries}</span>
          <div className="flex-1 h-1.5 bg-cream rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-saffron" style={{ width: `${(row.inquiries / maxInquiries) * 100}%` }} />
          </div>
          <span className="text-[10px] text-earth/60 w-10 text-right">{row.share}%</span>
        </div>
      </td>
      <td className={`${CELL_CLS} font-semibold text-green-600`} data-testid={`country-won-${slugOf(row.country)}`}>{row.won}</td>
      <td className={CELL_CLS}>{row.open}</td>
      <td className={CELL_CLS}>{row.lost}</td>
      <td className={CELL_CLS}>{row.hot}</td>
      <td className={`${CELL_CLS} font-semibold text-earth-dark`} data-testid={`country-conversion-${slugOf(row.country)}`}>{row.conversion}%</td>
      <td className={CELL_CLS}>{row.top_product || '—'}</td>
      <td className={CELL_CLS}>{formatDay(row.last_inquiry)}</td>
    </tr>
  );
}

export default function CountryTable({ countries, sortKey, onSort }) {
  const rows = sortCountries(countries, sortKey);
  const maxInquiries = Math.max(1, ...countries.map(c => c.inquiries));
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-5" data-testid="country-table">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-sm font-bold text-earth-dark">All Buyer Countries</h3>
        <SortPills value={sortKey} onChange={onSort} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {['Country', 'Inquiries', 'Won', 'Open', 'Lost', 'Hot', 'Conversion', 'Top product', 'Last inquiry'].map(h => <th key={h} className={HEAD_CLS}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => <CountryRow key={row.country} row={row} maxInquiries={maxInquiries} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
